import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
} from '@mui/material';
import {
  Warning,
  OpenInNew,
  CloudUpload,
  Edit,
  Save,
  Close,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Card } from '../../types';
import api from '../../services/api';

interface PilotDetailsModalProps {
  open: boolean;
  onClose: () => void;
  card: Card | null;
  onRefresh?: () => Promise<void>;
}

type DocumentType = 'attestato' | 'adesione' | 'pagamento';

interface EditableFields {
  nome: string;
  cognome: string;
  data_nascita: string;
  luogo_nascita: string;
  provincia_nascita: string;
  codice_fiscale: string;
  indirizzo: string;
  cap: string;
  luogo_residenza: string;
  provincia_residenza: string;
  telefono: string;
  attestato_nr: string;
  tipo: string;
  scadenza_visita_medica: string;
  altro: string;
}

const PilotDetailsModal: React.FC<PilotDetailsModalProps> = ({
  open,
  onClose,
  card,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploading, setUploading] = useState<DocumentType | null>(null);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedFields, setEditedFields] = useState<EditableFields>({
    nome: '',
    cognome: '',
    data_nascita: '',
    luogo_nascita: '',
    provincia_nascita: '',
    codice_fiscale: '',
    indirizzo: '',
    cap: '',
    luogo_residenza: '',
    provincia_residenza: '',
    telefono: '',
    attestato_nr: '',
    tipo: '',
    scadenza_visita_medica: '',
    altro: '',
  });

  // Federation membership editing state
  const [editingFederation, setEditingFederation] = useState(false);
  const [federationNumber, setFederationNumber] = useState('');
  const [savingFederation, setSavingFederation] = useState(false);

  // Refs for file inputs
  const attestatoInputRef = useRef<HTMLInputElement>(null);
  const adesioneInputRef = useRef<HTMLInputElement>(null);
  const pagamentoInputRef = useRef<HTMLInputElement>(null);

  // Initialize edited fields when card changes or edit mode is entered
  useEffect(() => {
    if (card && isEditMode) {
      setEditedFields({
        nome: card.nome || '',
        cognome: card.cognome || '',
        data_nascita: card.data_nascita ? card.data_nascita.split('T')[0] : '',
        luogo_nascita: card.luogo_nascita || '',
        provincia_nascita: card.provincia_nascita || '',
        codice_fiscale: card.codice_fiscale || '',
        indirizzo: card.indirizzo || '',
        cap: card.cap?.toString() || '',
        luogo_residenza: card.luogo_residenza || '',
        provincia_residenza: card.provincia_residenza || '',
        telefono: card.telefono || '',
        attestato_nr: card.attestato_nr || '',
        tipo: card.tipo || '',
        scadenza_visita_medica: card.scadenza_visita_medica ? card.scadenza_visita_medica.split('T')[0] : '',
        altro: card.altro || '',
      });
    }
  }, [card, isEditMode]);

  const handleClose = () => {
    setError(null);
    setSuccess(null);
    setIsEditMode(false);
    setEditingFederation(false);
    onClose();
  };

  const handleFieldChange = (field: keyof EditableFields) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditedFields(prev => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleStartEdit = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedFields({
      nome: '',
      cognome: '',
      data_nascita: '',
      luogo_nascita: '',
      provincia_nascita: '',
      codice_fiscale: '',
      indirizzo: '',
      cap: '',
      luogo_residenza: '',
      provincia_residenza: '',
      telefono: '',
      attestato_nr: '',
      tipo: '',
      scadenza_visita_medica: '',
      altro: '',
    });
  };

  const handleSaveChanges = async () => {
    if (!card) return;

    setSaving(true);
    setError(null);

    try {
      // Build payload with only changed fields
      const payload: Record<string, any> = {};

      if (editedFields.nome !== (card.nome || '')) {
        payload.nome = editedFields.nome || null;
      }
      if (editedFields.cognome !== (card.cognome || '')) {
        payload.cognome = editedFields.cognome || null;
      }
      if (editedFields.data_nascita !== (card.data_nascita?.split('T')[0] || '')) {
        payload.data_nascita = editedFields.data_nascita || null;
      }
      if (editedFields.luogo_nascita !== (card.luogo_nascita || '')) {
        payload.luogo_nascita = editedFields.luogo_nascita || null;
      }
      if (editedFields.provincia_nascita !== (card.provincia_nascita || '')) {
        payload.provincia_nascita = editedFields.provincia_nascita || null;
      }
      if (editedFields.codice_fiscale !== (card.codice_fiscale || '')) {
        payload.codice_fiscale = editedFields.codice_fiscale || null;
      }
      if (editedFields.indirizzo !== (card.indirizzo || '')) {
        payload.indirizzo = editedFields.indirizzo || null;
      }
      if (editedFields.cap !== (card.cap?.toString() || '')) {
        payload.cap = editedFields.cap ? parseInt(editedFields.cap, 10) : null;
      }
      if (editedFields.luogo_residenza !== (card.luogo_residenza || '')) {
        payload.luogo_residenza = editedFields.luogo_residenza || null;
      }
      if (editedFields.provincia_residenza !== (card.provincia_residenza || '')) {
        payload.provincia_residenza = editedFields.provincia_residenza || null;
      }
      if (editedFields.telefono !== (card.telefono || '')) {
        payload.telefono = editedFields.telefono || null;
      }
      if (editedFields.attestato_nr !== (card.attestato_nr || '')) {
        payload.attestato_nr = editedFields.attestato_nr || null;
      }
      if (editedFields.tipo !== (card.tipo || '')) {
        payload.tipo = editedFields.tipo || null;
      }
      if (editedFields.scadenza_visita_medica !== (card.scadenza_visita_medica?.split('T')[0] || '')) {
        payload.scadenza_visita_medica = editedFields.scadenza_visita_medica || null;
      }
      if (editedFields.altro !== (card.altro || '')) {
        payload.altro = editedFields.altro || null;
      }

      // Only make API call if there are changes
      if (Object.keys(payload).length > 0) {
        await api.put(`/cards/${card.record_id}/update`, payload);
        setSuccess(t('cards.edit.success'));

        if (onRefresh) {
          await onRefresh();
        }
      }

      setIsEditMode(false);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('cards.edit.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleFileDownload = async (filename: string | undefined, fileType: string) => {
    if (!card || !filename) return;

    try {
      const response = await api.get(`/download-reg-data/${card.record_id}/${filename}`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] || 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');

      setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
    } catch (err: any) {
      setError(`${t('cards.pilotDetails.downloadFailed')} ${fileType}`);
    }
  };

  const handleFileUpload = async (documentType: DocumentType, file: File) => {
    if (!card) return;

    setUploading(documentType);
    setError(null);
    setSuccess(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      await api.post(
        `/cards/${card.record_id}/upload-document?document_type=${documentType}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setSuccess(t('cards.pilotDetails.uploadSuccess'));

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || t('cards.pilotDetails.uploadFailed'));
    } finally {
      setUploading(null);
    }
  };

  const handleFileInputChange = (documentType: DocumentType) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(documentType, file);
    }
    event.target.value = '';
  };

  const triggerFileUpload = (documentType: DocumentType) => {
    // Check if federation membership is required for this document type
    if ((documentType === 'adesione' || documentType === 'pagamento') && !card?.adesione_nr) {
      setError(t('cards.pilotDetails.federationNumberRequired'));
      return;
    }

    const inputRef = {
      attestato: attestatoInputRef,
      adesione: adesioneInputRef,
      pagamento: pagamentoInputRef,
    }[documentType];

    inputRef.current?.click();
  };

  const handleStartEditFederation = () => {
    setFederationNumber(card?.adesione_nr?.toString() || '');
    setEditingFederation(true);
  };

  const handleCancelEditFederation = () => {
    setEditingFederation(false);
    setFederationNumber('');
  };

  const handleSaveFederation = async () => {
    if (!card) return;

    // Allow empty value to clear the federation number
    const trimmedValue = federationNumber.trim();
    let numValue: number | null = null;

    if (trimmedValue) {
      numValue = parseInt(trimmedValue, 10);
      if (isNaN(numValue)) {
        setError(t('cards.pilotDetails.invalidFederationNumber'));
        return;
      }
    }

    setSavingFederation(true);
    setError(null);

    try {
      await api.put(`/cards/${card.record_id}/federation-membership`, {
        adesione_nr: numValue,
      });

      setSuccess(t('cards.pilotDetails.federationSaved'));
      setEditingFederation(false);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || t('cards.pilotDetails.federationSaveFailed'));
    } finally {
      setSavingFederation(false);
    }
  };

  if (!card) return null;

  // Editable InfoRow component
  const EditableInfoRow = ({
    label,
    value,
    field,
    highlight = false,
    type = 'text',
    readOnly = false,
  }: {
    label: string;
    value: string | React.ReactNode;
    field?: keyof EditableFields;
    highlight?: boolean;
    type?: 'text' | 'date';
    readOnly?: boolean;
  }) => (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      {isEditMode && field && !readOnly ? (
        <TextField
          size="small"
          fullWidth
          type={type}
          value={editedFields[field]}
          onChange={handleFieldChange(field)}
          disabled={saving}
          InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        />
      ) : (
        <Box
          sx={{
            color: highlight ? 'error.main' : 'inherit',
            fontWeight: highlight ? 600 : 400,
            typography: 'body1',
          }}
        >
          {value || '-'}
        </Box>
      )}
    </Box>
  );

  const DocumentCard = ({
    url,
    label,
    fileType,
    documentType,
    disabled = false,
    disabledReason,
  }: {
    url?: string;
    label: string;
    fileType: string;
    documentType: DocumentType;
    disabled?: boolean;
    disabledReason?: string;
  }) => {
    const filename = url ? url.split('/').pop() || url : null;
    const isUploading = uploading === documentType;
    const hasFile = !!filename;

    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: disabled ? 'grey.200' : hasFile ? 'success.light' : 'grey.300',
          borderRadius: 2,
          p: 2,
          bgcolor: disabled ? 'grey.50' : hasFile ? 'success.50' : 'background.paper',
          opacity: disabled ? 0.7 : 1,
          transition: 'all 0.2s',
          '&:hover': disabled ? {} : {
            borderColor: hasFile ? 'success.main' : 'primary.light',
            boxShadow: 1,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2" color={disabled ? 'text.disabled' : 'text.primary'} sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Chip
            size="small"
            label={hasFile ? t('cards.pilotDetails.uploaded') : t('cards.pilotDetails.missing')}
            color={hasFile ? 'success' : 'default'}
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>

        {disabled && disabledReason && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {disabledReason}
          </Typography>
        )}

        {hasFile ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<OpenInNew sx={{ fontSize: 16 }} />}
              onClick={() => handleFileDownload(filename, fileType)}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {t('cards.pilotDetails.view')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={isUploading ? <CircularProgress size={14} /> : <CloudUpload sx={{ fontSize: 16 }} />}
              onClick={() => triggerFileUpload(documentType)}
              disabled={isUploading || disabled}
              sx={{ textTransform: 'none', fontSize: '0.75rem' }}
            >
              {t('cards.pilotDetails.replace')}
            </Button>
          </Box>
        ) : (
          <Button
            size="small"
            variant="contained"
            startIcon={isUploading ? <CircularProgress size={14} color="inherit" /> : <CloudUpload sx={{ fontSize: 16 }} />}
            onClick={() => triggerFileUpload(documentType)}
            disabled={isUploading || disabled}
            sx={{ mt: 1, textTransform: 'none', fontSize: '0.75rem' }}
          >
            {t('cards.pilotDetails.uploadFile')}
          </Button>
        )}
      </Box>
    );
  };

  const isMedicalVisitExpired = card?.scadenza_visita_medica
    ? new Date(card.scadenza_visita_medica) < new Date()
    : false;

  const hasFederationNumber = !!card.adesione_nr;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      {/* Hidden file inputs */}
      <input
        type="file"
        ref={attestatoInputRef}
        onChange={handleFileInputChange('attestato')}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={adesioneInputRef}
        onChange={handleFileInputChange('adesione')}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
        style={{ display: 'none' }}
      />
      <input
        type="file"
        ref={pagamentoInputRef}
        onChange={handleFileInputChange('pagamento')}
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp"
        style={{ display: 'none' }}
      />

      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h5">{t('cards.pilotDetails.title')}</Typography>
            {!isEditMode && (
              <Tooltip title={t('cards.pilotDetails.editDetails')}>
                <IconButton size="small" onClick={handleStartEdit} color="primary">
                  <Edit />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Chip
            label={card.active ? t('cards.pilotDetails.active') : t('cards.pilotDetails.inactive')}
            color={card.active ? 'success' : 'default'}
            size="small"
          />
        </Box>
        {isEditMode && (
          <Alert severity="info" sx={{ mt: 1 }}>
            {t('cards.pilotDetails.editModeInfo')}
          </Alert>
        )}
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {isMedicalVisitExpired && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>{t('cards.pilotDetails.warning')}:</strong> {t('cards.pilotDetails.medicalExpiredWarning')}
          </Alert>
        )}

        {/* Basic IDs */}
        <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('cards.pilotDetails.recordId')}:</strong> {card.record_id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('cards.pilotDetails.clubId')}:</strong> {card.club_id}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>{t('cards.pilotDetails.year')}:</strong> {card.year}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
          {/* Anagrafica */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              {t('cards.pilotDetails.personalInfo')}
            </Typography>
            <EditableInfoRow label={t('cards.pilotDetails.id')} value={card.id.toString()} readOnly />
            <EditableInfoRow label={t('cards.pilotDetails.firstName')} value={card.nome} field="nome" />
            <EditableInfoRow label={t('cards.pilotDetails.lastName')} value={card.cognome} field="cognome" />
            <EditableInfoRow label={t('cards.pilotDetails.birthProvince')} value={card.provincia_nascita} field="provincia_nascita" />
            <EditableInfoRow label={t('cards.pilotDetails.birthPlace')} value={card.luogo_nascita} field="luogo_nascita" />
            <EditableInfoRow
              label={t('cards.pilotDetails.dateOfBirth')}
              value={card.data_nascita ? format(new Date(card.data_nascita), 'dd/MM/yyyy') : '-'}
              field="data_nascita"
              type="date"
            />
            <EditableInfoRow label={t('cards.pilotDetails.taxCode')} value={card.codice_fiscale} field="codice_fiscale" />
            <EditableInfoRow label={t('cards.pilotDetails.email')} value={card.email} readOnly />
            <EditableInfoRow label={t('cards.pilotDetails.phone')} value={card.telefono} field="telefono" />
          </Box>

          {/* Informazioni sulla Residenza */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              {t('cards.pilotDetails.residenceInfo')}
            </Typography>
            <EditableInfoRow label={t('cards.pilotDetails.residenceProvince')} value={card.provincia_residenza} field="provincia_residenza" />
            <EditableInfoRow label={t('cards.pilotDetails.residenceCity')} value={card.luogo_residenza} field="luogo_residenza" />
            <EditableInfoRow label={t('cards.pilotDetails.address')} value={card.indirizzo} field="indirizzo" />
            <EditableInfoRow label={t('cards.pilotDetails.postalCode')} value={card.cap?.toString()} field="cap" />
          </Box>
        </Box>

        {/* Altre Informazioni */}
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            {t('cards.pilotDetails.otherInfo')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
            <Box>
              <EditableInfoRow label={t('cards.pilotDetails.certificateNumber')} value={card.attestato_nr} field="attestato_nr" />
              <EditableInfoRow label={t('cards.pilotDetails.type')} value={card.tipo} field="tipo" />
              <EditableInfoRow
                label={t('cards.pilotDetails.medicalVisitExpiry')}
                value={
                  !isEditMode && card.scadenza_visita_medica
                    ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {isMedicalVisitExpired && <Warning sx={{ color: 'error.main', fontSize: 20 }} />}
                          <span>{format(new Date(card.scadenza_visita_medica), 'dd/MM/yyyy')}</span>
                          {isMedicalVisitExpired && <Chip label={t('cards.pilotDetails.expired')} color="error" size="small" sx={{ ml: 1 }} />}
                        </Box>
                      )
                    : (card.scadenza_visita_medica ? format(new Date(card.scadenza_visita_medica), 'dd/MM/yyyy') : '-')
                }
                field="scadenza_visita_medica"
                type="date"
                highlight={isMedicalVisitExpired}
              />
              <EditableInfoRow label={t('cards.pilotDetails.federationMembership')} value={card.fivl ? t('cards.pilotDetails.yes') : t('cards.pilotDetails.no')} readOnly />
            </Box>
            <Box>
              {/* Editable Federation Membership Number */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  {t('cards.pilotDetails.federationMembershipNumber')}
                </Typography>
                {editingFederation ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      value={federationNumber}
                      onChange={(e) => setFederationNumber(e.target.value)}
                      placeholder={t('cards.pilotDetails.enterFederationNumber')}
                      sx={{ flex: 1 }}
                      autoFocus
                    />
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={handleSaveFederation}
                      disabled={savingFederation}
                    >
                      {savingFederation ? <CircularProgress size={20} /> : <Save />}
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEditFederation}
                      disabled={savingFederation}
                    >
                      <Close />
                    </IconButton>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body1">
                      {card.adesione_nr?.toString() || '-'}
                    </Typography>
                    {!isEditMode && (
                      <Tooltip title={t('cards.pilotDetails.editFederationNumber')}>
                        <IconButton size="small" onClick={handleStartEditFederation}>
                          <Edit sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
              <EditableInfoRow label={t('cards.pilotDetails.insuranceCode')} value={card.codice} readOnly />
            </Box>
          </Box>
          <Box sx={{ mt: 2 }}>
            <EditableInfoRow label={t('cards.pilotDetails.otherQualifications')} value={card.altro} field="altro" />
          </Box>
        </Box>

        {/* Uploaded Files Section */}
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            {t('cards.pilotDetails.uploadedDocuments')}
          </Typography>

          {/* First row: License Certificate */}
          <Box sx={{ mb: 2 }}>
            <DocumentCard
              url={card.attestato_file_url}
              label={t('cards.pilotDetails.licenseCertificate')}
              fileType={t('cards.pilotDetails.licenseCertificate')}
              documentType="attestato"
            />
          </Box>

          {/* Second row: Federation Membership and Payment Receipt */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
            <DocumentCard
              url={card.adesione_file_url}
              label={t('cards.pilotDetails.federationMembershipFileType')}
              fileType={t('cards.pilotDetails.federationMembershipFileType')}
              documentType="adesione"
              disabled={!hasFederationNumber}
              disabledReason={!hasFederationNumber ? t('cards.pilotDetails.setFederationNumberFirst') : undefined}
            />
            <DocumentCard
              url={card.pagamento_file_url}
              label={t('cards.pilotDetails.paymentReceipt')}
              fileType={t('cards.pilotDetails.paymentReceipt')}
              documentType="pagamento"
              disabled={!hasFederationNumber}
              disabledReason={!hasFederationNumber ? t('cards.pilotDetails.setFederationNumberFirst') : undefined}
            />
          </Box>
        </Box>

        {/* Status Information and Dates */}
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
            {t('cards.pilotDetails.datesAndStatus')}
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 2 }}>
            <Box>
              <EditableInfoRow
                label={t('cards.pilotDetails.createdDate')}
                value={format(new Date(card.created_at), 'dd/MM/yyyy HH:mm')}
                readOnly
              />
              <EditableInfoRow
                label={t('cards.pilotDetails.paid')}
                value={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontWeight: 600, color: card.pagato ? 'success.main' : 'error.main' }}>
                      {card.pagato ? t('cards.pilotDetails.yes') : t('cards.pilotDetails.no')}
                    </Box>
                  </Box>
                }
                readOnly
              />
              <EditableInfoRow
                label={t('cards.pilotDetails.paymentDate')}
                value={card.pagato_at ? format(new Date(card.pagato_at), 'dd/MM/yyyy HH:mm') : t('cards.pilotDetails.notAvailable')}
                readOnly
              />
            </Box>
            <Box>
              <EditableInfoRow
                label={t('cards.pilotDetails.cardGenerated')}
                value={card.card_generated_at ? format(new Date(card.card_generated_at), 'dd/MM/yyyy HH:mm') : t('cards.pilotDetails.notAvailable')}
                readOnly
              />
              <EditableInfoRow
                label={t('cards.pilotDetails.emailSent')}
                value={card.email_sent_at ? format(new Date(card.email_sent_at), 'dd/MM/yyyy HH:mm') : t('cards.pilotDetails.notAvailable')}
                readOnly
              />
              <EditableInfoRow
                label={t('cards.pilotDetails.federationEmailSent')}
                value={card.email_fivl_sent_at ? format(new Date(card.email_fivl_sent_at), 'dd/MM/yyyy HH:mm') : t('cards.pilotDetails.notAvailable')}
                readOnly
              />
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', alignItems: 'center', gap: 2 }}>
          {isEditMode ? (
            <>
              <Button onClick={handleCancelEdit} variant="outlined" disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSaveChanges}
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} /> : <Save />}
              >
                {t('common.save')}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} variant="contained">
              {t('cards.pilotDetails.close')}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PilotDetailsModal;
